/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	Text,
	Button,
	TextField,
	ErrorText,
	Divider,
	Icon,
	Pressable,
	Checkbox,
} from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import { HomeSideNav, Footer } from '@components/systems'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { blue, deepPurple, grey, pink } from '@mui/material/colors'
import { CustomerType } from '@schemas'
import { produce } from 'immer'
import config from '@configs'
import { HomeNavMenuType } from '@redux/types'

const CustomerInfoPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const reduxCustomer: CustomerType = useAppSelector(state => state.customerAuth.customer)
	const isEditCustomerInfo: boolean = useAppSelector(
		state => state.customerAuth.isEditCustomerInfo,
	)
	const updateCustomerErrorMessage: string = useAppSelector(
		state => state.customerAuth.updateCustomerErrorMessage,
	)
	const modalInfo: ModalInfoType = useAppSelector(
		state => state.customerAuth.customerInfoModalInfo,
	)
	const showCustomerInfoModal: boolean = useAppSelector(
		state => state.customerAuth.showCustomerInfoModal,
	)

	// state
	const [customer, setCustomer] = React.useState({
		email: '',
		name: '',
		confirmEmailNewsYn: 0,
	})
	const [isEmailVerification, setIsEmailVerification] = React.useState<boolean>(false)
	const [emailVerificationCode, setEmailVerificationCode] = React.useState<string>('')

	const menuList: HomeNavMenuType[] = config.customerMenu()

	React.useEffect(() => {
		setCustomer({
			name: reduxCustomer.name,
			email: reduxCustomer.email,
			confirmEmailNewsYn: reduxCustomer.confirmEmailNewsYn,
		})
	}, [reduxCustomer])

	const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		const newCustomer = produce(customer, draft => {
			draft.name = str
		})

		setCustomer(newCustomer)
	}

	const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		const newCustomer = produce(customer, draft => {
			draft.email = str
		})

		setCustomer(newCustomer)
	}

	const onEmailVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value

		setEmailVerificationCode(str)
	}

	const onVerificationCodeSubmit = () => {
		reduxDispatch(customerAuthActions.VERIFICATION_EMAIL({ code: emailVerificationCode }))
	}

	const onConfirmEmailNewsYn = () => {
		if (isEditCustomerInfo) {
			const newCustomer = produce(customer, draft => {
				draft.confirmEmailNewsYn = customer.confirmEmailNewsYn === 1 ? 0 : 1
			})

			setCustomer(newCustomer)
		}
	}

	const onModalSubmit = () => {
		if (modalInfo.target === 'emailVerification') {
			reduxDispatch(customerAuthActions.TRIGGER_SEND_EMAIL_VERIFICATION_CODE(true))
			setIsEmailVerification(true)
		}
	}

	const onSave = () => {
		// 이메일을 바꾸면 인증이 풀린다 — 저장과 동시에 인증 코드 입력을 열어 둔다.
		// (코드 발송은 saga 의 updateCustomerSaga 가 이어서 처리한다)
		const isEmailChanged: boolean = customer.email !== reduxCustomer.email

		reduxDispatch(customerAuthActions.UPDATE_CUSTOMER(customer))

		if (isEmailChanged) {
			setIsEmailVerification(true)
		}
	}

	return (
		<View css={style}>
			<View className="outerView">
				<HomeSideNav menu={menuList} selectedIndes={0} />

				<View className="contentOuterView">
					<Text category="h1">My Info</Text>
					<View className="contentView">
						<View className={`customerInfoView ${isEditCustomerInfo ? 'edit' : ''}`}>
							<View className="customerInfoLabelView">
								<Text category="s1">Name</Text>
							</View>
							<View className="customerInfoValueView">
								{isEditCustomerInfo ? (
									<TextField
										type="text"
										label="Your Name"
										size="small"
										defaultValue={reduxCustomer.name}
										onChange={onNameChange}
									/>
								) : (
									<Text category="h5">{reduxCustomer.name}</Text>
								)}
							</View>
						</View>

						<View className={`customerInfoView ${isEditCustomerInfo ? 'edit' : ''}`}>
							<View className="customerInfoLabelView">
								<Text category="s1">Email</Text>
							</View>
							<View className="customerInfoValueView">
								{isEditCustomerInfo ? (
									<>
										<TextField
											type="text"
											label="Email"
											size="small"
											defaultValue={reduxCustomer.email}
											onChange={onEmailChange}
										/>
										<View className="confirmEmailNewsYnView">
											<Pressable
												onClick={onConfirmEmailNewsYn}
												className="confirmEmailNewsYnButton"
											>
												<Checkbox checked={customer.confirmEmailNewsYn === 1} />
												<Text category="p1">
													Yes, I’d like to receive updates on the latest guitar gear and
													discounts.
												</Text>
											</Pressable>
										</View>
									</>
								) : (
									<View className="customerEmailOuterView">
										<View className="customerEmailView">
											<Text category="h5">{reduxCustomer.email}</Text>
											{!isEmailVerification && reduxCustomer.verifiedYn === 0 && (
												<View className="emailVerificationButtonView">
													<Button
														variant="contained"
														className="tryVerificationButton"
														onClick={() => {
															reduxDispatch(
																customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
																	target: 'emailVerification',
																	title: 'Email Verification',
																}),
															)
															reduxDispatch(
																customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true),
															)
														}}
													>
														Verification
													</Button>
												</View>
											)}

											{!isEmailVerification && reduxCustomer.verifiedYn === 1 && (
												<View className="emailVerifiedView">
													<View className="emailVerifiedIconView">
														<Icon name="check-bold" className="emailVerifiedIcon" />
													</View>
													<Text className="verified">Verified</Text>
												</View>
											)}
										</View>
									</View>
								)}
							</View>
						</View>

						{reduxCustomer.verifiedYn === 0 && (
							<View className="emailVerificationWarningView">
								{isEmailVerification && (
									<View>
										<Divider className="emailVerificationDivider" />

										<TextField
											type="text"
											label="Email Verification Code"
											size="small"
											onChange={onEmailVerificationCodeChange}
										/>

										<View className="verificationButtonView">
											<Button
												variant="contained"
												className="cancelVerificationButton"
												onClick={() => setIsEmailVerification(false)}
											>
												Cancel
											</Button>
											<Button
												variant="contained"
												className="verificationButton"
												disabled={emailVerificationCode === ''}
												onClick={onVerificationCodeSubmit}
											>
												Verify
											</Button>
										</View>
									</View>
								)}

								{!isEmailVerification && (
									<Text className="emailVerificationWarning">
										If this email is not verified, you will not be able to view
										information via email, such as order information, shipping
										information, and password reset.
									</Text>
								)}
							</View>
						)}

						{!isEmailVerification && (
							<View className="bottomView">
								{updateCustomerErrorMessage !== '' ? (
									<ErrorText className="errorMsg">{updateCustomerErrorMessage}</ErrorText>
								) : null}

								{isEditCustomerInfo ? (
									<>
										<Button
											className="cancelButton"
											variant="contained"
											onClick={() =>
												reduxDispatch(customerAuthActions.IS_EDIT_CUSTOMER_INFO(false))
											}
										>
											Cancel
										</Button>
										<Button className="saveButton" variant="contained" onClick={onSave}>
											Save
										</Button>
									</>
								) : (
									<Button
										className="editButton"
										variant="contained"
										onClick={() =>
											reduxDispatch(customerAuthActions.IS_EDIT_CUSTOMER_INFO(true))
										}
									>
										Edit
									</Button>
								)}
							</View>
						)}
					</View>
				</View>
			</View>

			<Footer />

			<Modal
				target={modalInfo.target ?? ''}
				title={modalInfo.title}
				show={showCustomerInfoModal}
				onCloseModal={() => {
					reduxDispatch(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(false))

					if (modalInfo.target === 'verificationSuccess') {
						setIsEmailVerification(false)
					}
				}}
				closeStatus="inherit"
				showSubmit={modalInfo.target === 'emailVerification'}
				closeTitle={modalInfo.target === 'updateSuccess' ? 'Close' : 'Cancel'}
				submitTitle="Get Code"
				submitIconColor={deepPurple['A400']}
				isTitleDanger={modalInfo.target === 'verificationFailed'}
				onSubmit={onModalSubmit}
			>
				{modalInfo.target === 'updateSuccess' ? (
					<View css={{ width: '30rem' }}>
						<Text category="p2">Your information has been successfully updated.</Text>
					</View>
				) : modalInfo.target === 'emailVerification' ? (
					<View>
						<Text category="p2">
							{"To verify this email, please click the 'Get Code' button below."}
						</Text>
					</View>
				) : modalInfo.target === 'verificationCodeSent' ? (
					<View>
						<Text category="p2">{"'Verification code' has been sent."}</Text>
						<Text category="p2">Please check your registered email.</Text>
					</View>
				) : modalInfo.target === 'verificationSuccess' ? (
					<View>
						<Text category="p2">Verification has been success.</Text>
					</View>
				) : modalInfo.target === 'verificationFailed' ? (
					<View>
						<Text category="p2">Please check your code again.</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

export default CustomerInfoPage

const style = css({
	position: 'relative',
	width: '100%',

	'.outerView': {
		display: 'flex',
		paddingTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey[300],

		'.contentOuterView': {
			width: 'calc(100% - 15rem)',
			minHeight: 'calc(100vh - 10rem)',
			padding: '2rem',
			borderLeft: '1px solid',
			borderColor: grey[300],

			'.contentView': {
				width: '100%',
				maxWidth: '50rem',
				right: 0,
				left: 0,
				margin: 'auto',
				paddingTop: '3rem',
				borderTop: '1px solid',
				borderColor: grey[500],

				'.customerInfoView': {
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'flex-start',
					minHeight: '4rem',

					'.customerInfoLabelView': {
						width: '10rem',
						marginRight: '2rem',
						border: '1px solid',
					},
					'.customerInfoValueView': {
						width: '38rem',
						textAlign: 'left',

						'.customerEmailOuterView': {
							'.customerEmailView': {
								display: 'flex',
								justifyContent: 'flex-start',
								alignItems: 'center',

								'.emailVerificationButtonView': {
									marginLeft: '1rem',
									'.tryVerificationButton': {
										background: grey[900],
									},
								},

								'.emailVerifiedView': {
									display: 'flex',
									justifyContent: 'center',
									alignItems: 'center',
									marginLeft: '1rem',

									'.emailVerifiedIconView': {
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										width: '1.3rem',
										height: '1.3rem',
										background: blue['A400'],
										borderRadius: '20px',

										'.emailVerifiedIcon': {
											color: 'white',
											fontSize: '18px',
										},
									},

									'.verified': {
										marginTop: '0.5rem',
										marginBottom: '0.5rem',
										marginLeft: '0.2rem',
										color: blue['A400'],
									},
								},
							},
						},

						'.confirmEmailNewsYnView': {
							'.confirmEmailNewsYnButton': {
								display: 'flex',
								justifyContent: 'flex-start',
								textAlign: 'left',
							},
						},
					},
				},

				'.emailVerificationWarningView': {
					paddingLeft: '12rem',

					'.verificationButtonView': {
						marginTop: '1rem',
						marginBottom: '2rem',
						textAlign: 'right',

						'.cancelVerificationButton': {
							marginRight: '1rem',
							background: 'white',
							color: grey[900],
						},
						'.verificationButton': {},
					},

					'.emailVerificationWarning': {
						textAlign: 'left',
						fontWeight: '500',
						lineHeight: '20px',
						color: pink['A700'],
					},

					'.emailVerificationDivider': {
						marginTop: '2rem',
						marginBottom: '2rem',
					},
				},

				'.bottomView': {
					marginTop: '5rem',

					'.cancelButton': {
						minWidth: '10rem',
						marginRight: '0.5rem',
						background: grey[100],
						color: grey[900],
					},
					'.saveButton': {
						minWidth: '10rem',
						background: grey[900],
					},
					'.editButton': {
						minWidth: '10rem',
						background: grey[900],
					},
				},
			},
		},
	},

	'@media (max-width: 890px)': {
		'.outerView': {
			'.contentOuterView': {
				width: '100%',
				borderLeft: '0px',
			},
		},
	},

	'@media (max-width: 460px)': {
		'.outerView': {
			'.contentOuterView': {
				'.contentView': {
					'.customerInfoView': {
						display: 'block',
						marginBottom: '2rem',

						'.customerInfoLabelView': {
							width: '100%',
							border: '0px',
							textAlign: 'left',
							p: {
								margin: 0,
								color: grey[500],
							},
						},
						'.customerInfoValueView': {
							width: '100%',

							'.customerEmailOuterView': {
								'.customerEmailView': {
									display: 'block',
								},
							},
						},

						'&.edit': {
							'.customerInfoLabelView': {
								display: 'none',
							},
						},
					},

					'.bottomView': {
						'.cancelButton': {
							minWidth: '0px',
							width: 'calc(50% - 2rem)',
						},

						'.saveButton': {
							minWidth: '0px',
							width: 'calc(50% - 2rem)',
						},
					},
				},
			},
		},
	},
})
